import React, { useState, useEffect, useCallback } from 'react';
import { getStoredToken } from '../services/authSession';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  AlertTriangle, 
  CheckCircle2, 
  XCircle, 
  RefreshCw, 
  TrendingUp,
  TrendingDown,
  Activity,
  Shield,
  Clock,
  FileCheck
} from 'lucide-react';
import API from '../services/apiBase';
import { useClient } from '../context/ClientContext';

const ReconciliationDashboard = () => {
  const { clientId } = useClient();
  const token = getStoredToken();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reconciliationData, setReconciliationData] = useState(null);
  const [history, setHistory] = useState([]);
  const [lastChecked, setLastChecked] = useState(null);

  const fetchReconciliationStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (clientId) params.set('client_id', clientId);
      const response = await fetch(`${API}/data/reconciliation/status${params.toString() ? `?${params.toString()}` : ''}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(clientId ? { 'X-Client-Id': clientId } : {}),
        },
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || 'Failed to fetch reconciliation status');
      }
      const data = await response.json();
      setReconciliationData(data);
      setLastChecked(new Date().toLocaleTimeString());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, token]);

  const fetchHistory = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '5' });
      if (clientId) params.set('client_id', clientId);
      const response = await fetch(`${API}/data/reconciliation/history?${params.toString()}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(clientId ? { 'X-Client-Id': clientId } : {}),
        },
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.history || []);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  }, [clientId, token]);

  useEffect(() => {
    fetchReconciliationStatus();
    fetchHistory();
  }, [fetchReconciliationStatus, fetchHistory]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'bg-emerald-500';
      case 'warning': return 'bg-amber-500';
      case 'critical': return 'bg-red-500';
      default: return 'bg-slate-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
      case 'warning': return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case 'critical': return <XCircle className="h-5 w-5 text-red-500" />;
      default: return <Activity className="h-5 w-5 text-slate-500" />;
    }
  };

  const getSeverityBadge = (severity) => {
    const variants = {
      ok: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      info: 'bg-blue-100 text-blue-800 border-blue-200',
      warning: 'bg-amber-100 text-amber-800 border-amber-200',
      critical: 'bg-red-100 text-red-800 border-red-200'
    };
    return (
      <Badge className={`${variants[severity] || variants.ok} border`}>
        {severity.toUpperCase()}
      </Badge>
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(4)}%`;
  };

  if (loading && !reconciliationData) {
    return (
      <div className="p-6 space-y-6" data-testid="reconciliation-dashboard-loading">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            <span className="ml-3 text-slate-500">Loading reconciliation data...</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6" data-testid="reconciliation-dashboard-error">
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={fetchReconciliationStatus} className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" /> Retry
        </Button>
      </div>
    );
  }

  const summary = reconciliationData?.summary || {};
  const metrics = reconciliationData?.metrics || [];
  const alerts = reconciliationData?.alerts || [];
  const overallStatus = reconciliationData?.overall_status || 'unknown';

  return (
    <div className="p-6 space-y-6" data-testid="reconciliation-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Data Insights</h1>
          <p className="text-slate-500 mt-1">Early warning system for GL/Schedule H divergences</p>
        </div>
        <div className="flex items-center gap-4">
          {lastChecked && (
            <span className="text-sm text-slate-500 flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              Last checked: {lastChecked}
            </span>
          )}
          <Button 
            onClick={fetchReconciliationStatus} 
            disabled={loading}
            data-testid="refresh-reconciliation-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overall Status Card */}
      <Card className={`border-l-4 ${
        overallStatus === 'healthy' ? 'border-l-emerald-500' :
        overallStatus === 'warning' ? 'border-l-amber-500' : 'border-l-red-500'
      }`} data-testid="overall-status-card">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${getStatusColor(overallStatus)}/10`}>
                {getStatusIcon(overallStatus)}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900 capitalize">
                  {overallStatus === 'healthy' ? 'All Systems Reconciled' :
                   overallStatus === 'warning' ? 'Divergences Detected' : 'Critical Issues Found'}
                </h2>
                <p className="text-slate-500">{reconciliationData?.recommendation}</p>
              </div>
            </div>
            <div className="flex gap-6 text-center">
              <div>
                <div className="text-2xl font-bold text-emerald-600">{summary.healthy || 0}</div>
                <div className="text-xs text-slate-500">Healthy</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-amber-600">{summary.warnings || 0}</div>
                <div className="text-xs text-slate-500">Warnings</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">{summary.critical || 0}</div>
                <div className="text-xs text-slate-500">Critical</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div className="space-y-3" data-testid="alerts-section">
          {alerts.map((alert, idx) => (
            <Alert 
              key={idx} 
              variant={alert.level === 'critical' ? 'destructive' : 'default'}
              className={alert.level === 'warning' ? 'border-amber-300 bg-amber-50' : ''}
            >
              {alert.level === 'critical' ? 
                <XCircle className="h-4 w-4" /> : 
                <AlertTriangle className="h-4 w-4 text-amber-600" />
              }
              <AlertTitle className={alert.level === 'warning' ? 'text-amber-800' : ''}>
                {alert.metric}
              </AlertTitle>
              <AlertDescription className={alert.level === 'warning' ? 'text-amber-700' : ''}>
                {alert.message} — {alert.action}
              </AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="metrics" className="w-full">
        <TabsList>
          <TabsTrigger value="metrics" data-testid="metrics-tab">
            <Shield className="h-4 w-4 mr-2" />
            Metrics ({metrics.length})
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="history-tab">
            <Clock className="h-4 w-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="metrics" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Reconciliation Metrics</CardTitle>
              <CardDescription>
                Comparison between GL calculations and Schedule H aggregations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table data-testid="metrics-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Metric</TableHead>
                    <TableHead className="text-right">GL Value</TableHead>
                    <TableHead className="text-right">Schedule H Value</TableHead>
                    <TableHead className="text-right">Difference</TableHead>
                    <TableHead className="text-right">Variance %</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {metrics.map((metric, idx) => (
                    <TableRow key={idx} data-testid={`metric-row-${idx}`}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{metric.name}</div>
                          <div className="text-xs text-slate-500">{metric.description}</div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {typeof metric.gl_value === 'number' && metric.gl_value > 1 
                          ? formatCurrency(metric.gl_value) 
                          : metric.gl_value?.toFixed?.(6) || metric.gl_value}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {typeof metric.schedule_h_value === 'number' && metric.schedule_h_value > 1 
                          ? formatCurrency(metric.schedule_h_value) 
                          : metric.schedule_h_value?.toFixed?.(6) || metric.schedule_h_value}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        <span className={
                          metric.difference > 0 ? 'text-red-600' :
                          metric.difference < 0 ? 'text-emerald-600' : ''
                        }>
                          {metric.difference !== 0 && (
                            metric.difference > 0 ? 
                              <TrendingUp className="h-3 w-3 inline mr-1" /> :
                              <TrendingDown className="h-3 w-3 inline mr-1" />
                          )}
                          {typeof metric.difference === 'number' && Math.abs(metric.difference) > 1
                            ? formatCurrency(metric.difference)
                            : metric.difference}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatPercentage(metric.percentage)}
                      </TableCell>
                      <TableCell className="text-center">
                        {getSeverityBadge(metric.severity)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Reconciliation History</CardTitle>
              <CardDescription>
                Track reconciliation status over recent calculation runs
              </CardDescription>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileCheck className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No reconciliation history available</p>
                </div>
              ) : (
                <Table data-testid="history-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>GL Run ID</TableHead>
                      <TableHead>Schedule H ID</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-center">Alerts</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((item, idx) => (
                      <TableRow key={idx} data-testid={`history-row-${idx}`}>
                        <TableCell className="font-mono text-sm">
                          {item.gl_run_id?.substring(0, 20)}...
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {item.schedule_h_id?.substring(0, 8)}...
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={
                            item.overall_status === 'healthy' ? 'bg-emerald-100 text-emerald-800' :
                            item.overall_status === 'warning' ? 'bg-amber-100 text-amber-800' :
                            'bg-red-100 text-red-800'
                          }>
                            {item.overall_status?.toUpperCase()}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {item.alerts_count || 0}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Thresholds Info */}
      <Card className="bg-slate-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Threshold Configuration</span>
          </div>
          <div className="flex gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
              <span className="text-slate-600">Healthy: &lt; {reconciliationData?.thresholds?.info || 0.5}%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <span className="text-slate-600">Warning: {reconciliationData?.thresholds?.info || 0.5}% - {reconciliationData?.thresholds?.critical || 5}%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-slate-600">Critical: &gt; {reconciliationData?.thresholds?.critical || 5}%</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReconciliationDashboard;
